import { Field, Form, Formik } from 'formik';
import { merge, get } from 'lodash';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Label } from 'office-ui-fabric-react/lib/Label';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import useAPI from '../../hooks/useAPI';
import { fetchAllRoles } from '../../actions/roles';
import { createUser, editUser } from '../../actions/users';
import { search } from '../../actions/search';

import DataItem from '../DataItem';
import Dropdown from '../Dropdown';
import SearchBar from '../SearchBar';
import MultiPickerFormik from '../MultiPicker/MultiPickerFormik';
import RolePermissionGrid from '../RolePermissionGrid';
import StackGrid from '../StackGrid';
import SubModule from '../SubModule';
import SubModuleContent from '../SubModuleContent';
import SubModuleHeader from '../SubModuleHeader';
import TextField from '../TextField';
import styles from './CreateEditUserForm.style';

const getRoles = () =>
  useSelector(state =>
    Object.values(get(state, 'entities.roles', {})).filter(
      role => role.permissions && Object.values(role.permissions).length > 0,
    ),
  );

const mapRolesToRoleOptions = roles => {
  return roles.map(role => ({
    key: role.slug,
    text: role.name,
  }));
};

const getRoleSelectorOptions = () => {
  return mapRolesToRoleOptions(getRoles());
};

const transformUser = user => ({
  ...user,
  secondaryRoles: mapRolesToRoleOptions(user.secondaryRoles),
  partnerSecondaryRole:
    user.role === 'partner' && user.secondaryRoles.length ? user.secondaryRoles[0].name : '',
});

const UserSchema = Yup.object().shape({
  mail: Yup.string().email('Invalid email').required('Required'),
  role: Yup.string().required('Required'),
  secondaryRoles: Yup.array(
    Yup.object().shape({
      key: Yup.string().required('Required'),
      text: Yup.string().required('Required'),
    }),
  ),
  partnerSecondaryRole: Yup.string().when('role', {
    is: 'partner',
    then: Yup.string().required('Required'),
  }),
});

const CreatEditUserForm = React.forwardRef(({ user, onSuccess, onFailure }, ref) => {
  const initialValues = merge(
    {
      displayName: '',
      mail: '',
      role: '',
      secondaryRoles: [],
    },
    user && transformUser(user),
  );

  const dispatch = useDispatch();
  const [showPartnerSuggestions, setShowPartnerSuggestions] = useState(true);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('');

  useEffect(() => {
    dispatch(fetchAllRoles());
  }, [dispatch]);

  const roleOptions = getRoleSelectorOptions();
  const roles = useSelector(state => get(state, `entities.roles`, null));
  const { data: suggestions, isLoading } = useAPI(
    () => search(['partnerUsers'], partnerQuery),
    [partnerQuery],
    {
      fetchOnMount: false,
      autoFetch: true,
    },
  );
  const primaryRoles = [];
  const secondaryRoles = [];
  roleOptions.forEach(role => {
    if (['msft', 'partner'].includes(role.key)) {
      primaryRoles.push(role);
    } else if (role.key !== 'admin') {
      // Do not display admin as a role option
      secondaryRoles.push(role);
    }
  });

  const partnerSuggestions = get(suggestions, 'partnerUsers', [])
    .map(partner => ({
      icon: 'People',
      value: partner.partnerRole,
      label: `${partner.name} (${partner.partnerRole})`,
    }))
    .slice(0, 5);

  const getPrimaryRoleName = roleParam => {
    const roleObj = primaryRoles.filter(role => role.key === roleParam);
    return roleObj[0].text;
  };

  const submitHandler = async (values, { setErrors }) => {
    let response;

    // 1 . Gather payload
    const formSecondaryRoles =
      values.role === 'partner'
        ? values.secondaryRoleForPartner.map(role => ({
            slug: role.key,
            name: role.text,
          }))
        : values.secondaryRoles.map(role => ({
            slug: role.key,
            name: role.text,
          }));
    const formValues = {
      role: values.role,
      secondaryRoles: formSecondaryRoles,
    };
    if (user) {
      formValues.oid = user.oid;
    } else {
      formValues.mail = values.mail;
    }

    // 2 . Call api
    if (user) {
      // Case - Edit user
      response = await dispatch(editUser(formValues, user.oid));
    } else {
      // Case - Add user
      response = await dispatch(createUser(formValues));
    }

    // 3. Display error messages from api , if any, on the UI
    if (response.error && response.payload.message) {
      const error = new Error(response.payload.message);
      error.payload = response.payload.message;
      setErrors({ form: error.message });
      await onFailure(response);
      return response;
    }

    response = get(response, `payload.entities.users.${get(response, 'payload.result')}`, {});

    // 4.Call onSuccess callback TODO - For create user case, need to get oid from the response after
    await onSuccess(response.oid);

    return response;
  };

  const getRolesWithPermissions = appliedRoles => {
    let selectedRolesWithPerm = [];
    selectedRolesWithPerm = appliedRoles.map(appliedRole => {
      return roles[appliedRole.key];
    });
    return selectedRolesWithPerm;
  };

  const handlePartnerSearch = newInput => {
    setPartnerQuery(newInput);
    setShowPartnerSuggestions(true);
  };

  const getSuggestions = () => {
    if (!showPartnerSuggestions) {
      return null;
    }
    return partnerSuggestions;
  };

  return (
    primaryRoles.length > 0 && (
      <Formik
        innerRef={ref}
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={UserSchema}
        enableReinitialize
        onSubmit={submitHandler}
      >
        {({ values, errors, isSubmitting, setFieldValue }) => {
          const handlePartnerSearchClear = () => {
            setFieldValue('partnerSecondaryRole', '');
            setFieldValue('secondaryRoleForPartner', []);
            setSelectedPartner('');
          };

          const onPartnerSelection = partner => {
            const secondaryRoleForPartner = [
              {
                key: partner.value,
                text: partner.label,
              },
            ];
            setFieldValue('partnerSecondaryRole', `${partner.label} User`);
            setFieldValue('secondaryRoleForPartner', secondaryRoleForPartner);
            setSelectedPartner(partner.label);
            setShowPartnerSuggestions(false);
          };

          return (
            <Form noValidate>
              {errors.form && (
                <MessageBar
                  messageBarType={MessageBarType.error}
                  isMultiline
                  dismissButtonAriaLabel="Close"
                  nowrap
                >
                  {errors.form}
                </MessageBar>
              )}
              <SubModule disableAccordian>
                <SubModuleHeader>General Information</SubModuleHeader>
                <SubModuleContent>
                  <StackGrid smCols={1} mdCols={1} lgCols={1} shrink>
                    {user ? (
                      <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                        {values.displayName && (
                          <DataItem label="Display Name">{values.displayName}</DataItem>
                        )}
                        {values.mail && <DataItem label="Email">{values.mail}</DataItem>}
                        {values.role && (
                          <DataItem label="Role">{getPrimaryRoleName(values.role)}</DataItem>
                        )}
                        {values.partnerSecondaryRole && (
                          <DataItem label="Secondary Role">{values.partnerSecondaryRole}</DataItem>
                        )}
                      </StackGrid>
                    ) : (
                      <StackGrid smCols={1} mdCols={2} lgCols={2} shrink>
                        <Field
                          name="mail"
                          label="Email"
                          component={TextField}
                          disabled={isSubmitting}
                          data-testid="email-selector"
                          required
                        />
                        <Field
                          name="role"
                          label="Role"
                          options={primaryRoles}
                          component={Dropdown}
                          disabled={isSubmitting}
                          required
                          data-testid="role-selector"
                        />
                      </StackGrid>
                    )}
                  </StackGrid>
                </SubModuleContent>
              </SubModule>

              {/** Secondary role section for msft users */}
              {values.role === 'msft' && (
                <>
                  <SubModule disableAccordian>
                    <SubModuleHeader>Secondary Role Assignment</SubModuleHeader>
                    <SubModuleContent>
                      <StackGrid smCols={1} mdCols={1} lgCols={1} shrink>
                        <Field
                          name="secondaryRoles"
                          label="Select secondary role(s) for the user"
                          options={secondaryRoles}
                          component={MultiPickerFormik}
                          disabled={isSubmitting}
                          multiTag
                          data-testid="secondary-role-selector"
                        />
                      </StackGrid>
                    </SubModuleContent>
                  </SubModule>

                  {values.secondaryRoles.length > 0 && (
                    <SubModule disableAccordian>
                      <SubModuleHeader>Permissions</SubModuleHeader>
                      <SubModuleContent>
                        <StackGrid smCols={1} mdCols={1} lgCols={1} shrink>
                          <RolePermissionGrid
                            roles={getRolesWithPermissions(values.secondaryRoles)}
                          />
                        </StackGrid>
                      </SubModuleContent>
                    </SubModule>
                  )}
                </>
              )}

              {/** Secondary role section for partner user */}
              {values.role === 'partner' && !user && (
                <SubModule disableAccordian>
                  <SubModuleHeader>Secondary Role Assignment</SubModuleHeader>
                  <SubModuleContent>
                    <StackGrid smCols={1} mdCols={user ? 1 : 2} lgCols={user ? 1 : 2} shrink>
                      <Label>
                        Search Partner (by PartnerName/ContractId/VendorId)
                        <SearchBar
                          isVisible={!user}
                          placeholder="Search Partner"
                          ariaLabel="Search"
                          underlined
                          name="partner-search"
                          onClick={onPartnerSelection}
                          value={selectedPartner}
                          onChange={handlePartnerSearch} // when you type
                          onClear={handlePartnerSearchClear} // when you click X
                          suggestions={getSuggestions()}
                          isLoading={isLoading}
                          data-testid="search-partner"
                          className={styles.search}
                        />
                      </Label>
                      <Field
                        name="partnerSecondaryRole"
                        label="Secondary role for the user"
                        component={TextField}
                        disabled
                        data-testid="secondary-role-selector"
                        required
                      />
                    </StackGrid>
                  </SubModuleContent>
                </SubModule>
              )}
            </Form>
          );
        }}
      </Formik>
    )
  );
});

CreatEditUserForm.propTypes = {
  user: PropTypes.shape({
    displayName: PropTypes.string,
    oid: PropTypes.string,
    mail: PropTypes.string,
    role: PropTypes.string,
    secondaryRoles: PropTypes.array,
  }),
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
};

CreatEditUserForm.defaultProps = {
  user: null,
  onSuccess: () => {},
  onFailure: () => {},
};

export default CreatEditUserForm;
